/**
 * LanguageContext — Provides language switching and translation functions.
 * Supports English (en) and Swahili (sw).
 * Stores preference in localStorage.
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import en from './en.json';
import sw from './sw.json';

export type Language = 'en' | 'sw';

const translations: Record<Language, Record<string, any>> = { en, sw };

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  t: (key: string) => key,
});

export const useLanguage = () => useContext(LanguageContext);

/**
 * Get nested value from object using dot notation.
 * e.g., getNestedValue(obj, 'dashboard.admin.title')
 */
function getNestedValue(obj: any, path: string): string | undefined {
  const keys = path.split('.');
  let current = obj;
  for (const key of keys) {
    if (current === null || current === undefined) return undefined;
    current = current[key];
  }
  return typeof current === 'string' ? current : undefined;
}

/**
 * Replace {{param}} placeholders in translation strings.
 */
function interpolate(template: string, params: Record<string, string | number>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => {
    return params[key] !== undefined ? String(params[key]) : `{{${key}}}`;
  });
}

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem('bhealth_language');
    return (stored === 'en' || stored === 'sw') ? stored : 'en';
  });

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('bhealth_language', lang);
    // Set document lang attribute for accessibility
    document.documentElement.lang = lang;
  }, []);

  const t = useCallback((key: string, params?: Record<string, string | number>): string => {
    const value = getNestedValue(translations[language], key);
    if (value === undefined) {
      // Fallback to English if key not found in current language
      const fallback = getNestedValue(translations.en, key);
      if (fallback !== undefined) {
        return params ? interpolate(fallback, params) : fallback;
      }
      return key; // Return key itself as last resort
    }
    return params ? interpolate(value, params) : value;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
